// @flow strict
import * as React from 'react'
import cx from 'classnames'
import {
  BusinessButton,
  ResponsiveImage,
  TrackEvents,
  AccessibilityText,
} from '@mondough/public-web-components'
import Dialog from '@reach/dialog'

import styles from '../HeroSection.module.scss'
import editorialStyles from './BusinessHero.module.scss'

import { MarkdownContent, ContentThemes } from '../MarkdownContent'
import { SectionWrapper, SectionThemes } from '../SectionWrapper'
import {
  buildHeroImageSources,
  buildMobileImageSources,
  type HeroSectionProps,
  buildDefaultImage,
} from '../HeroSection'

const PlayButton = ({ onClick }) => (
  <div className={editorialStyles.playButtonWrapper}>
    <button onClick={onClick} className={editorialStyles.playButton}>
      <AccessibilityText>Watch video</AccessibilityText>
      <svg width="18" height="24" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M.379.701l17.677 11.678L.38 24.056z"
          fill="#FFF"
          fillRule="evenodd"
        />
      </svg>
    </button>
  </div>
)

const BusinessHeroSection = ({
  title,
  description,
  imageUrls,
  imageAlts,
  flags,
  heroDescription,
  cta: { href = '/download', text = 'Join Monzo', category, action, label },
}: HeroSectionProps) => {
  const { desktop, mobile } = imageUrls
  const { theme = 'light', videoID } = flags || {}

  const desktopImageSources = buildHeroImageSources({ desktop }, '1850')
  const mobileImageSources = buildMobileImageSources(mobile)
  const hasVideo = videoID != null && videoID !== ''

  const [showVideo, setShowVideo] = React.useState(false)

  return (
    <SectionWrapper
      className={cx(styles.section, editorialStyles.section)}
      contentClassName={cx(styles.hero, editorialStyles.hero)}
      theme={theme === 'dark' ? SectionThemes.DARK : SectionThemes.LIGHT}
    >
      <ResponsiveImage
        className={cx(styles.image, editorialStyles.backgroundImage)}
        defaultImage={buildDefaultImage({ desktop }, '1900')}
        sources={desktopImageSources}
      />
      <div
        className={cx(styles.content, editorialStyles.content)}
        data-testid="HeroSection"
      >
        <div className={styles.text}>
          <h1 className={cx(styles.title, editorialStyles.title)}>{title}</h1>
          <MarkdownContent
            className={editorialStyles.subtitle}
            theme={theme === 'dark' ? ContentThemes.DARK : ContentThemes.LIGHT}
          >
            {description}
          </MarkdownContent>
          <MarkdownContent
            theme={theme === 'dark' ? ContentThemes.DARK : ContentThemes.LIGHT}
            className={editorialStyles.heroCaption}
            ariaHidden={true}
          >
            {heroDescription}
          </MarkdownContent>
          <div className={styles.cta}>
            <TrackEvents category={category} action={action} label={label}>
              <BusinessButton
                theme="secondary"
                href={href}
                className={editorialStyles.button}
              >
                {text}
              </BusinessButton>
            </TrackEvents>
          </div>
        </div>
        {hasVideo && <PlayButton onClick={() => setShowVideo(true)} />}
      </div>
      <ResponsiveImage
        className={editorialStyles.mobileHero}
        defaultImage={buildDefaultImage({ desktop: mobile || desktop })}
        sources={mobileImageSources}
      />
      {showVideo && flags && flags.videoID && (
        <Dialog
          className={editorialStyles.modal}
          aria-label="Customer testimonial video"
          onDismiss={() => setShowVideo(false)}
        >
          <div className={editorialStyles.videoWrapper}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoID}?modestbranding=1&rel=0`}
              frameBorder="0"
              allowFullScreen
              allow="autoplay; encrypted-media; picture-in-picture"
              title="Customer testimonial video"
            />
          </div>
        </Dialog>
      )}
    </SectionWrapper>
  )
}

export default BusinessHeroSection
